import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
	Sheet,
	SheetContent,
	SheetDescription,
	SheetHeader,
	SheetTitle,
	SheetTrigger,
} from "@/components/ui/sheet";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@/components/ui/dialog";
import {
	Select,
	SelectContent,
	SelectGroup,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import {
	FileText,
	Upload,
	Download,
	Eye,
	Trash,
	Clock,
	CheckCircle,
	XCircle,
	FileCheck,
	Loader2,
	Mail,
	Calendar,
	User,
	ArrowRight,
	AlertCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/axiosInstance';
import Helper from '@/lib/helper';
import { Skeleton } from '@/components/ui/skeleton';
import { useNavigate } from 'react-router-dom';
import { Spinner } from '@/components/ui/shadcn-io/spinner';
import type { PropertyListings } from '@/types/PropertyListings';
import type { PropertyListingDocuments } from '@/types/PropertyListingDocuments';
import type { PropertyListingDocumentSigners } from '@/types/PropertyListingDocumentSigners';

interface DocumentTemplate {
	[key: string]: string;
}

interface DocumentsProps {
	listing: PropertyListings;
}

const Documents: React.FC<DocumentsProps> = ({ listing }) => {
	const [documents, setDocuments] = useState<PropertyListingDocuments[]>([]);
	const [templates, setTemplates] = useState<DocumentTemplate>({});
	const [loading, setLoading] = useState(true);
	const [templatesLoading, setTemplatesLoading] = useState(true);
	const [showTemplateSheet, setShowTemplateSheet] = useState(false);
	const [showUploadDialog, setShowUploadDialog] = useState(false);
	const [showTimelineDialog, setShowTimelineDialog] = useState(false);
	const [showPreviewDialog, setShowPreviewDialog] = useState(false);
	const [showDeleteDialog, setShowDeleteDialog] = useState(false);
	const [selectedDocument, setSelectedDocument] = useState<PropertyListingDocuments | null>(null);
	const [documentToDelete, setDocumentToDelete] = useState<PropertyListingDocuments | null>(null);
	const [selectedTemplate, setSelectedTemplate] = useState<string>('');
	const [uploading, setUploading] = useState(false);
	const [selectedFile, setSelectedFile] = useState<File | null>(null);
	const [pdfUrl, setPdfUrl] = useState<string | null>(null);
	const [loadingPdf, setLoadingPdf] = useState<boolean>(false);
	const [downloadingId, setDownloadingId] = useState<number | null>(null);
	const navigate = useNavigate();

	const fetchDocuments = async () => {
		try {
			setLoading(true);
			const response = await api.get(`/listings/${listing.id}/documents`);
			if (!response.data.status) {
				throw new Error(response.data.error);
			}
			setDocuments(response.data.documents || []);
		} catch (err: any) {
			const error = Helper.errorHandler(err);
			toast.error(error);
		} finally {
			setLoading(false);
		}
	};

	const getSignedDocument = async (id: number) => {
		try {
			setLoadingPdf(true);
			const response = await api.get(`/listings/${listing.id}/documents/${id}`, {
				responseType: 'blob'
			});
			
			const blob = new Blob([response.data], { type: 'application/pdf' });
			const url = window.URL.createObjectURL(blob);
			setPdfUrl(url);
		} catch (err) {
			const error = Helper.errorHandler(err);
			toast.error(error);
		} finally {
			setLoadingPdf(false);
		}
	}

	const downloadPDF = async (doc?: PropertyListingDocuments) => {
		try {
			if(!doc) return;
			setDownloadingId(doc.id);
			const response = await api.get(`/listings/${listing.id}/documents/${doc.id}`, {
				responseType: 'blob'
			});
			
			const blob = new Blob([response.data], { type: 'application/pdf' });
			const url = window.URL.createObjectURL(blob);
			
			const link = document.createElement('a');
			link.href = url;
			link.download = `${getTemplateName(doc.documentTemplateId)}.pdf`;
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			
			window.URL.revokeObjectURL(url);
		} catch (err) {
			const error = Helper.errorHandler(err);
			toast.error(error);
		} finally {
			setDownloadingId(null);
		}
	}

	const getDocumentTemplates = async () => {
		try {
			setTemplatesLoading(true);
			const response = await api.get(`/listings/documents/templates`);
			if (!response.data.status) {
				throw new Error(response.data.error);
			}
			setTemplates(response.data.templates || {});
		} catch (err: any) {
			const error = Helper.errorHandler(err);
			toast.error(error);
		} finally {
			setTemplatesLoading(false);
		}
	};

	const getNextSigner = (doc: PropertyListingDocuments): PropertyListingDocumentSigners | null => {
		console.log(doc)
		if (!doc.envelopeId || !doc.signers) return null;
		
		if (doc.status === 'completed') return null;
		
		const sortedSigners = [...doc.signers].sort(
			(a, b) => (a.id) - (b.id)
		);
	
		return sortedSigners.find(
			signer => signer.status !== 'completed' && signer.status !== 'signed'
		) || null;
	};

	const getSignerStatusBadge = (status: string) => {
		const statusMap = {
			created: { color: 'bg-gray-50 text-gray-700 border-gray-300', icon: Clock, label: 'Created' },
			sent: { color: 'bg-gray-50 text-gray-700 border-gray-300', icon: Mail, label: 'Sent' },
			delivered: { color: 'bg-gray-50 text-gray-700 border-gray-300', icon: Mail, label: 'Delivered' },
			signed: { color: 'bg-green-50 text-green-700 border-green-300', icon: CheckCircle, label: 'Signed' },
			completed: { color: 'bg-green-50 text-green-700 border-green-300', icon: CheckCircle, label: 'Completed' },
			declined: { color: 'bg-red-50 text-red-700 border-red-300', icon: XCircle, label: 'Declined' },
			authentication_failed: { color: 'bg-red-50 text-red-700 border-red-300', icon: AlertCircle, label: 'Auth Failed' },
		};

		const config = statusMap[status as keyof typeof statusMap] || statusMap.created;
		const Icon = config.icon;

		return (
			<Badge variant="outline" className={config.color}>
				<Icon className="w-3 h-3 mr-1" />
				{config.label}
			</Badge>
		);
	};

	const getEnvelopeStatusBadge = (status: string) => {
		switch (status) {
			case 'sent':
				return (
					<Badge variant="outline" className="bg-gray-50 text-gray-700 border-gray-300">
						<Mail className="w-3 h-3 mr-1" />
						Sent
					</Badge>
				);
			case 'delivered':
				return (
					<Badge variant="outline" className="bg-gray-50 text-gray-700 border-gray-300">
						<Mail className="w-3 h-3 mr-1" />
						Delivered
					</Badge>
				);
			case 'completed':
				return (
					<Badge variant="outline" className="bg-green-50 text-green-700 border-green-300">
						<CheckCircle className="w-3 h-3 mr-1" />
						Completed
					</Badge>
				);
			case 'declined':
			case 'voided':
				return (
					<Badge variant="outline" className="bg-red-50 text-red-700 border-red-300">
						<XCircle className="w-3 h-3 mr-1" />
						{status === 'declined' ? 'Declined' : 'Voided'}
					</Badge>
				);
			default:
				return (
					<Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-300">
						<Clock className="w-3 h-3 mr-1" />
						Pending
					</Badge>
				);
		}
	};

	const handleTemplateSelect = (key: string) => {
		setSelectedTemplate(key);
	};

	const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (file) {
			if (file.size > 10 * 1024 * 1024) {
				toast.error('File size must be less than 10MB');
				return;
			}
			setSelectedFile(file);
		}
	};

	const handleUploadDocument = async () => {
		if (!selectedFile) {
			toast.error('Please select a file');
			return;
		}

		if (!selectedTemplate) {
			toast.error('Please select a document template');
			return;
		}

		try {
			setUploading(true);
			const formData = new FormData();
			formData.append('file', selectedFile);
			formData.append('documentTemplateId', selectedTemplate);

			const response = await api.post(`/listings/${listing.id}/documents`, formData);
			if (!response.data.status) {
				throw new Error(response.data.error);
			}

			toast.success('Document uploaded successfully');
			setShowUploadDialog(false);
			setSelectedFile(null);
			setSelectedTemplate('');
			fetchDocuments();
		} catch (err: any) {
			const error = Helper.errorHandler(err);
			toast.error(error);
		} finally {
			setUploading(false);
		}
	};

	const handleDeleteDocument = async () => {
		if (!documentToDelete) return;
		try {
			const response = await api.delete(
				`/listings/${listing.id}/documents/${documentToDelete.id}`
			);
		
			if (!response.data.status) {
				throw new Error(response.data.error);
			}
		
			toast.success("Successfully deleted document");
			setShowDeleteDialog(false);
			setDocumentToDelete(null);
		
			setDocuments(prev => prev.filter(x => x.id !== documentToDelete.id));
		} catch (err: any) {
			const error = Helper.errorHandler(err);
			toast.error(error);
		}
	};

	const confirmDelete = (doc: PropertyListingDocuments) => {
		setDocumentToDelete(doc);
		setShowDeleteDialog(true);
	};

	const handleViewTimeline = (doc: PropertyListingDocuments) => {
		setSelectedDocument(doc);
		setShowTimelineDialog(true);
	};

	const handlePreviewDocument = (doc: PropertyListingDocuments) => {
		setSelectedDocument(doc);
		setShowPreviewDialog(true);
		getSignedDocument(doc.id);
	};
	
	const getTemplateName = (documentTemplateId: number) => {
		return templates[documentTemplateId] || 'Unknown Template';
	};

	// Fixed: Changed type from Document[] to ListingDocuments[]
	const groupedDocuments = documents.reduce((acc, doc) => {
		const templateId = doc.documentTemplateId;
		if (!acc[templateId]) {
			acc[templateId] = [];
		}
		acc[templateId].push(doc);
		return acc;
	}, {} as Record<number, PropertyListingDocuments[]>);

	useEffect(() => {
		getDocumentTemplates();
	}, []);

	useEffect(() => {
		if (!templatesLoading) {
			fetchDocuments();
		}
	}, [templatesLoading]);

	if (templatesLoading) {
		return (
			<div className="space-y-4">
				<div className="flex items-center justify-center p-12">
					<Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
					<span className="ml-3 text-muted-foreground">Loading templates...</span>
				</div>
			</div>
		);
	}

	if (loading) {
		return (
			<div className="space-y-6">
				<div className="flex flex-wrap gap-3">
					<Skeleton className="h-10 w-32" />
					<Skeleton className="h-10 w-40" />
				</div>
	
				{[1, 2].map((groupIndex) => (
					<div key={groupIndex} className="space-y-3">
						<div className="flex items-center gap-2">
							<Skeleton className="w-5 h-5 rounded" />
							<Skeleton className="h-6 w-48" />
							<Skeleton className="h-5 w-24 rounded-full" />
						</div>
						
						<div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
							{[1, 2, 3].map((cardIndex) => (
								<Card key={cardIndex}>
									<CardContent className="p-6">
										<div className="space-y-4">
											<div className="flex items-start gap-4">
												<Skeleton className="w-12 h-12 rounded-lg" />
												<div className="flex-1 space-y-2">
													<Skeleton className="h-4 w-20 rounded-full" />
													<Skeleton className="h-4 w-24 rounded-full" />
												</div>
											</div>
											
											<Skeleton className="h-16 w-full rounded-lg" />
											
											<Skeleton className="h-4 w-32" />
											
											<div className="flex gap-2 flex-wrap">
												<Skeleton className="h-8 w-20 rounded-md" />
												<Skeleton className="h-8 w-20 rounded-md" />
												<Skeleton className="h-8 w-24 rounded-md" />
												<Skeleton className="h-8 w-20 rounded-md" />
											</div>
										</div>
									</CardContent>
								</Card>
							))}
						</div>
					</div>
				))}
			</div>
		);
	}

	return (
		<div className="space-y-6">
			<div className="flex flex-wrap gap-3">
				<Sheet open={showTemplateSheet} onOpenChange={setShowTemplateSheet}>
					<SheetTrigger asChild>
						<Button>
							<FileText className="w-4 h-4 mr-2" />
							Fill Template
						</Button>
					</SheetTrigger>
					<SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto">
						<SheetHeader>
							<SheetTitle>Fill Document Template</SheetTitle>
							<SheetDescription>
								Select a template and provide recipient email
							</SheetDescription>
						</SheetHeader>
						<div className="mt-6 space-y-6">
							<div className="space-y-4 p-3">
								<Label>Select Template</Label>
								<div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
									{
										Object.keys(templates).length === 0 ? (
											<p className="text-sm text-muted-foreground text-center py-8 col-span-full">
												No templates available
											</p>
										) : (
											Object.entries(templates).map(([key, name]) => {
												let disabled = false;
												if (listing.forSaleOrRentId === 1 && (key === '2' || key === '4')) {
												  	disabled = true;
												} else if (listing.forSaleOrRentId === 2 && (key === '1' || key === '3')) {
												  	disabled = true;
												}

												return (
													<Card
													key={key}
													className={`cursor-pointer hover:border-primary disabled transition-colors ${
														selectedTemplate === key ? 'border-primary bg-primary/5' : ''
													} ${disabled ? 'opacity-50 cursor-not-allowed hover:border-gray-200' : ''}`}
													onClick={() => {
														if (disabled) return;
														const slug = templates[key].replaceAll(' ', '-').toLowerCase();
														navigate(`/documents/${slug}?listingId=${listing.id}`)
													}}>
														<CardContent className="p-4">
															<div className="flex items-center gap-3">
																<div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
																	<FileText className="w-5 h-5 text-gray-600" />
																</div>
																<div className="flex-1 min-w-0">
																	<h4 className="font-semibold text-sm">{name}</h4>
																</div>
															</div>
														</CardContent>
													</Card>
												)
											})
										)
									}
								</div>
							</div>
						</div>
					</SheetContent>
				</Sheet>
				<Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
					<DialogTrigger asChild>
						<Button variant="outline">
							<Upload className="w-4 h-4 mr-2" />
							Upload Document
						</Button>
					</DialogTrigger>
					<DialogContent>
						<DialogHeader>
							<DialogTitle>Upload Document</DialogTitle>
							<DialogDescription>
								Upload a completed document or form
							</DialogDescription>
						</DialogHeader>
						<div className="space-y-4 mt-4">
							<div className="space-y-2">
								<Label className="mb-2">Document Template</Label>
								<Select value={selectedTemplate} onValueChange={handleTemplateSelect}>
									<SelectTrigger>
										<SelectValue placeholder="Select a template" />
									</SelectTrigger>
									<SelectContent>
										<SelectGroup>
											{
												Object.keys(templates).length === 0 ? (
													<div className="text-sm text-muted-foreground text-center py-4">
														No templates available
													</div>
												) : (
													Object.entries(templates).map(([key, name]) => (
														<SelectItem key={key} value={key}>
															{name}
														</SelectItem>
													))
												)
											}
										</SelectGroup>
									</SelectContent>
								</Select>
							</div>
							<div className="space-y-2">
								<Label>Select File</Label>
								<div className="border-2 border-dashed rounded-lg p-6 text-center hover:border-primary transition-colors cursor-pointer">
									<input
									type="file"
									onChange={handleFileSelect}
									accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
									className="hidden"
									id="file-upload"/>
									<label htmlFor="file-upload" className="cursor-pointer">
										{
											selectedFile ? (
												<div className="space-y-2">
													<FileCheck className="w-12 h-12 mx-auto text-green-500" />
													<p className="text-sm font-medium">{selectedFile.name}</p>
													<p className="text-xs text-muted-foreground">
														{(selectedFile.size / 1024 / 1024).toFixed(2)} MB
													</p>
												</div>
											) : (
												<div className="space-y-2">
													<Upload className="w-12 h-12 mx-auto text-muted-foreground" />
													<p className="text-sm font-medium">Click to select file</p>
													<p className="text-xs text-muted-foreground">
														PDF, DOC, DOCX, JPG, PNG (Max 10MB)
													</p>
												</div>
											)
										}
									</label>
								</div>
							</div>
							<Button
							onClick={handleUploadDocument}
							disabled={uploading || !selectedFile || !selectedTemplate}
							className="w-full">
								{
									uploading ? (
										<Spinner />
									) : (
										<>
											<Upload className="w-4 h-4 mr-2" />
											Upload Document
										</>
									)
								}
							</Button>
						</div>
					</DialogContent>
				</Dialog>
			</div>
			<Dialog open={showTimelineDialog} onOpenChange={setShowTimelineDialog}>
				<DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
					<DialogHeader>
						<DialogTitle>Signing Timeline</DialogTitle>
						<DialogDescription>
							Track the progress of document signers
						</DialogDescription>
					</DialogHeader>
					{
						selectedDocument && selectedDocument.signers && (
							<div className="space-y-6 mt-4">
								<div className="flex items-center justify-between p-4 bg-muted rounded-lg">
									<div>
										<h4 className="font-semibold">{getTemplateName(selectedDocument.documentTemplateId)}</h4>
										<p className="text-sm text-muted-foreground">
											Created {Helper.formatDateTime(selectedDocument.createdAt)}
										</p>
									</div>
									{getEnvelopeStatusBadge(selectedDocument.status)}
								</div>

								<div className="space-y-4">
									<h5 className="font-semibold text-sm">Signers</h5>
									<div className="space-y-4">
										{selectedDocument.signers
											.sort((a, b) => (a.id) - (b.id))
											.map((signer, index) => (
												<div key={signer.id} className="relative">
													{index < selectedDocument.signers.length - 1 && (
														<div className="absolute left-6 top-12 bottom-0 w-0.5 bg-gray-200" />
													)}
													<div className="flex gap-4">
														<div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 relative z-10">
															<User className="w-6 h-6 text-primary" />
														</div>
														<div className="flex-1 pb-6">
															<div className="flex items-start justify-between mb-2">
																<div>
																	<h6 className="font-semibold">{signer.name}</h6>
																	<p className="text-sm text-muted-foreground">{signer.email}</p>
																	{signer.role && (
																		<p className="text-xs text-muted-foreground mt-1">
																			Role: {signer.role}
																		</p>
																	)}
																</div>
																{getSignerStatusBadge(signer.status)}
															</div>

															<div className="space-y-1 text-sm text-muted-foreground">
																{signer.sentAt && (
																	<div className="flex items-center gap-2">
																		<Mail className="w-3.5 h-3.5" />
																		<span>
																			Sent: {Helper.formatDateTime(signer.sentAt)}
																		</span>
																	</div>
																)}
																{signer.viewedAt && (
																	<div className="flex items-center gap-2">
																		<CheckCircle className="w-3.5 h-3.5" />
																		<span>
																			Viewed: {Helper.formatDateTime(signer.viewedAt)}
																		</span>
																	</div>
																)}
																{signer.completedAt && (
																	<div className="flex items-center gap-2">
																		<CheckCircle className="w-3.5 h-3.5 text-green-600" />
																		<span className="text-green-600 font-medium">
																			Signed: {Helper.formatDateTime(signer.completedAt)}
																		</span>
																	</div>
																)}
																{!signer.completedAt &&
																	signer.status !== 'completed' &&
																	signer.status !== 'signed' && (
																		<div className="flex items-center gap-2 text-yellow-600">
																			<Clock className="w-3.5 h-3.5" />
																			<span>Waiting for signature</span>
																		</div>
																	)}
															</div>
														</div>
													</div>
												</div>
											))}
									</div>
								</div>
								{selectedDocument.completedAt && (
									<div className="p-4 bg-green-50 border border-green-200 rounded-lg">
										<div className="flex items-center gap-2 text-green-700">
											<CheckCircle className="w-5 h-5" />
											<span className="font-semibold">
												Completed on {Helper.formatDateTime(selectedDocument.completedAt)}
											</span>
										</div>
									</div>
								)}
							</div>
						)
					}
					{
						selectedDocument && !selectedDocument.signers && (
							<div className="py-8 text-center text-muted-foreground">
								<p>No signing timeline available for this document.</p>
								<p className="text-sm mt-2">This document was manually uploaded.</p>
							</div>
						)
					}
				</DialogContent>
			</Dialog>

			<Dialog open={showPreviewDialog} onOpenChange={(open) => {
				setShowPreviewDialog(open);
				if (!open) {
					setPdfUrl(null);
					setLoadingPdf(false);
				}
			}}>
				<DialogContent className="max-w-5xl max-h-[95vh] p-0 flex flex-col">
					<div className="flex justify-between items-center p-4 border-b">
						<h3 className="text-lg font-semibold">Document Preview</h3>
					</div>

					<div className="flex-1 min-h-0 p-4">
						{loadingPdf ? (
							<div className="w-full h-[80vh] flex flex-col items-center justify-center bg-muted/30 rounded-lg">
								<Loader2 className="w-12 h-12 animate-spin text-primary mb-4" />
								<p className="text-sm font-medium text-muted-foreground">Loading document...</p>
								<p className="text-xs text-muted-foreground mt-1">Please wait while we prepare your preview</p>
							</div>
						) : pdfUrl ? (
							<iframe
								src={`${pdfUrl}#toolbar=1&navpanes=0&scrollbar=1`}
								className="w-full h-[80vh] border rounded"
								title="Document Preview"
							/>
						) : (
							<div className="w-full h-[80vh] flex flex-col items-center justify-center text-muted-foreground bg-muted/30 rounded-lg">
								<FileText className="w-16 h-16 mb-4 opacity-50" />
								<p className="text-sm font-medium">No document to preview</p>
							</div>
						)}
					</div>
				</DialogContent>
			</Dialog>

			<Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Delete Document</DialogTitle>
						<DialogDescription>
							Are you sure you want to delete this document? This action cannot be undone.
						</DialogDescription>
					</DialogHeader>

					{documentToDelete && (
						<div className="p-4 bg-muted rounded-lg">
							<div className="flex items-center gap-3">
								<div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
									<FileText className="w-5 h-5 text-gray-600" />
								</div>
								<div>
									<p className="font-semibold text-sm">
										{getTemplateName(documentToDelete.documentTemplateId)}
									</p>
									<p className="text-xs text-muted-foreground">
										Created {Helper.formatDateOnly(documentToDelete.createdAt)}
									</p>
								</div>
							</div>
						</div>
					)}

					<div className="flex gap-3 justify-end mt-4">
						<Button
						variant="outline"
						onClick={() => {
							setShowDeleteDialog(false);
							setDocumentToDelete(null);
						}}
						>
							Cancel
						</Button>
						<Button
						variant="destructive"
						onClick={handleDeleteDocument}
						>
							<Trash className="w-4 h-4 mr-2" />
							Delete Document
						</Button>
					</div>
				</DialogContent>
			</Dialog>

			{/* Documents List */}
			<div className="space-y-6">
				{documents.length === 0 ? (
					<div className="text-center py-12">
						<FileText className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
						<h3 className="text-lg font-semibold mb-2">No documents yet</h3>
						<p className="text-muted-foreground mb-4">
							Get started by filling a template or uploading a document
						</p>
					</div>
				) : (
					Object.entries(groupedDocuments).map(([templateId, docs]) => (
						<div key={templateId} className="space-y-3">
							<h3 className="font-semibold text-lg flex items-center gap-2">
								<FileText className="w-5 h-5 text-gray-600" />
								{getTemplateName(parseInt(templateId))}
								<Badge variant="outline" className="ml-2">
									{docs.length} {docs.length === 1 ? 'document' : 'documents'}
								</Badge>
							</h3>
							<div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
								{docs.map((doc, index) => {
									const nextSigner = doc.envelopeId ? getNextSigner(doc) : null;
									const isLatest = index === 0;
									const isDownloading = downloadingId === doc.id;
									
									return (
										<Card 
											key={doc.id}
											className={isLatest ? 'ring-2 ring-primary shadow-lg' : ''}
										>
											<CardContent className="p-6">
												<div className="space-y-4">
													{isLatest && (
														<Badge className="bg-primary text-primary-foreground">
															Latest Version
														</Badge>
													)}
													
													<div className="flex items-start gap-4">
														<div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
															<FileText className="w-6 h-6 text-gray-600" />
														</div>

														<div className="flex-1 min-w-0">
															<div className="flex items-center gap-2 flex-wrap">
																<Badge
																className={`text-xs ${
																	doc.envelopeId
																	? 'bg-green-100 text-green-700 border border-green-300'
																	: 'bg-gray-100 text-gray-700 border border-gray-300'
																}`}>
																{doc.envelopeId ? 'Digital' : 'Manual Upload'}
																</Badge>
																{doc.envelopeId && getEnvelopeStatusBadge(doc.status)}
															</div>
														</div>
													</div>
													{nextSigner && (
														<div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
															<ArrowRight className="w-4 h-4 text-gray-600 flex-shrink-0" />
															<div className="flex-1 min-w-0">
																<p className="text-xs font-medium text-gray-900">Next Signer</p>
																<p className="text-sm text-gray-700 truncate">{nextSigner.email}</p>
															</div>
														</div>
													)}
													
													<div className="flex items-center gap-2 text-xs text-muted-foreground">
														<Calendar className="w-3.5 h-3.5" />
														<span>Created {Helper.formatDateOnly(doc.createdAt)}</span>
													</div>
													
													<div className="flex gap-2 flex-wrap">
														{doc.envelopeId && (
															<Button
																size="sm"
																variant="outline"
																onClick={() => handleViewTimeline(doc)}>
																<Clock className="w-3.5 h-3.5 mr-1" />
																Timeline
															</Button>
														)}
														{doc.path && (
															<>
																<Button
																	size="sm"
																	variant="outline"
																	onClick={() => handlePreviewDocument(doc)}>
																	<Eye className="w-3.5 h-3.5 mr-1" />
																	Preview
																</Button>
																<Button
																	size="sm"
																	variant="outline"
																	onClick={() => downloadPDF(doc)}
																	disabled={isDownloading}>
																	{isDownloading ? <Spinner className="mr-1 h-3.5 w-3.5" /> : <Download className="w-3.5 h-3.5 mr-1" />}
																	{isDownloading ? 'Downloading...' : 'Download'}
																</Button>
															</>
														)}
														<Button
															size="sm"
															variant="outline"
															className="text-red-600 hover:text-red-700 hover:bg-red-50"
															onClick={() => confirmDelete(doc)}>
															<Trash className="w-3.5 h-3.5 mr-1" />
															Delete
														</Button>
													</div>
												</div>
											</CardContent>
										</Card>
									);
								})}
							</div>
						</div>
					))
				)}
			</div>
		</div>
	);
};

export default Documents;
